import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Upload, FileSpreadsheet, Check } from "lucide-react";
import { previewExcel, importExcel } from "../../lib/api";

const STD_FIELDS = [
  "", "Name", "URL", "Land", "Stadt", "Bundesland", "PLZ", "Anschrift",
  "E-Mail", "Telefon", "Geschäftsführung", "Anrede", "Titel", "Vorname",
  "Nachname", "Assets under Management (in Mio. EUR)", "Immobilien Fokus",
  "Immobilien Fokus: Details", "Zusätzlicher Fokus", "Kommentar",
];

const QUELLEN = [
  "BNP",
  "Listenchampion-MFO",
  "Listenchampion-SFO-Immobilien",
  "Listenchampion-Adel",
  "Listenchampion-Retail",
  "Heinrich-eigen",
];

export default function ImportModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [quelle, setQuelle] = useState("Heinrich-eigen");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setResult(null);
    try {
      const data = await previewExcel(f);
      setPreview(data);
      setMapping(data.auto_mapping || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Datei konnte nicht gelesen werden");
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await importExcel(file, quelle, mapping);
      setResult(res);
      toast.success(`Import: ${res.imported} neu, ${res.merged} zusammengeführt`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fehlgeschlagen");
    } finally { setImporting(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setMapping({}); setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/30 z-40 animate-fade-in-up" style={{animationDuration:"0.15s"}} />
      <div
        data-testid="import-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(900px,95vw)] max-h-[90vh] bg-white border border-navy/15 z-50 flex flex-col"
      >
        <div className="flex items-center justify-between border-b border-navy/10 px-6 py-4">
          <div>
            <div className="pb-eyebrow">Investoren-Import</div>
            <h3 className="font-serif text-navy text-2xl mt-1">Excel-Datei hochladen</h3>
          </div>
          <button data-testid="import-close" onClick={onClose} aria-label="Import schließen" className="text-navy/60 hover:text-navy min-w-[44px] min-h-[44px] -mr-2.5 inline-flex items-center justify-center"><X size={20}/></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Step 1: File + Quelle */}
          <section>
            <div className="pb-eyebrow mb-3">1 · Datei + Quelle</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="pb-label">Datei (.xlsx)</label>
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    data-testid="import-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="block w-full text-sm font-sans text-navy/80 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-navy file:text-white file:text-sm file:font-sans hover:file:bg-navy-600"
                  />
                  {file && (
                    <div className="mt-2 text-sm text-navy/70 font-sans inline-flex items-center gap-1.5">
                      <FileSpreadsheet size={14} className="text-gold"/>
                      {file.name} ({(file.size / 1024).toFixed(0)} kB)
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="pb-label">Quelle</label>
                <select
                  data-testid="import-quelle"
                  className="pb-input--full mt-2"
                  value={quelle}
                  onChange={(e) => setQuelle(e.target.value)}
                >
                  {QUELLEN.map((q) => <option key={q}>{q}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Step 2: Mapping Preview */}
          {preview && (
            <section>
              <div className="pb-eyebrow mb-3">2 · Spalten-Mapping &amp; Vorschau</div>
              <div className="border border-navy/15">
                <table className="pb-table">
                  <thead>
                    <tr>
                      <th>Excel-Spalte</th>
                      <th>Beispielwerte</th>
                      <th>Mappt auf →</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.header.map((col, idx) => (
                      <tr key={col + idx}>
                        <td className="font-sans text-sm text-navy/85">{col}</td>
                        <td className="font-sans text-xs text-navy/55 max-w-[280px] truncate">
                          {(preview.sample_rows[0]?.[idx] ?? "—")}{" "}
                          <span className="text-navy/35">| {preview.sample_rows[1]?.[idx] ?? "—"}</span>
                        </td>
                        <td>
                          <select
                            data-testid={`import-map-${idx}`}
                            value={mapping[col] || ""}
                            onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))}
                            className="pb-input--full text-xs py-1"
                          >
                            <option value="">— (ignorieren)</option>
                            {STD_FIELDS.filter(Boolean).map((f) => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Step 3: Ergebnis */}
          {result && (
            <section data-testid="import-result" className="pb-card bg-ledger border-l-[3px] border-l-gold">
              <div className="pb-eyebrow mb-2">Ergebnis</div>
              <div className="text-sm font-sans text-navy">
                <div><Check size={14} className="inline text-gold mr-1"/> <strong>{result.imported}</strong> Investoren neu importiert</div>
                <div><Check size={14} className="inline text-gold mr-1"/> <strong>{result.merged}</strong> mit bestehenden zusammengeführt (Quelle ergänzt)</div>
                <div><Check size={14} className="inline text-navy/40 mr-1"/> <strong>{result.skipped}</strong> übersprungen</div>
                {result.errors?.length > 0 && (
                  <details className="mt-3"><summary className="cursor-pointer text-navy/70">{result.errors.length} Fehler</summary>
                    <ul className="mt-2 text-xs text-navy/60 font-mono">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </details>
                )}
              </div>
            </section>
          )}
        </div>

        <div className="border-t border-navy/10 px-6 py-4 flex items-center justify-between">
          <button onClick={reset} className="pb-btn-ghost">Zurücksetzen</button>
          <div className="flex gap-3">
            <button onClick={onClose} className="pb-btn-outline">Schließen</button>
            <button
              data-testid="import-start-btn"
              onClick={handleImport}
              disabled={!file || importing}
              className="pb-btn-primary gap-2"
            >
              <Upload size={14}/> {importing ? "Importiere…" : "Import starten"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
